import { type JSX, Show, children, createUniqueId } from 'solid-js'
import { EmptyValue } from '../EmptyValue'
import { FIELD_MESSAGE_METRICS } from '../FieldError'

export type ReadOnlyValueSize = 'sm' | 'md'

export interface ReadOnlyValueProps {
  label?: string | undefined
  'aria-label'?: string | undefined
  value?: string | number | null | undefined
  hint?: string | undefined
  mutedLabel?: boolean | undefined
  size?: ReadOnlyValueSize | undefined
  inset?: boolean | undefined
  children?: JSX.Element
  class?: string | undefined
}

const valueSizeClasses: Record<ReadOnlyValueSize, string> = {
  sm: 'text-[length:var(--text-sm)]',
  md: 'text-[length:var(--text-base)]',
}

const insetClasses: Record<ReadOnlyValueSize, string> = {
  sm: 'min-h-9 px-3 py-2 border border-transparent',
  md: 'min-h-10 px-3.5 py-2 border border-transparent',
}

const hasText = (value: string | number | null | undefined): boolean =>
  typeof value === 'number' ? Number.isFinite(value) : (value ?? '').trim() !== ''

export function ReadOnlyValue(props: ReadOnlyValueProps) {
  const labelId = `read-only-value-label-${createUniqueId()}`
  const hintId = `read-only-value-hint-${createUniqueId()}`
  const content = children(() => props.children)

  const hasContent = () => Boolean(props.label) || Boolean(content()) || hasText(props.value)
  const size = () => props.size ?? 'md'
  const valueClass = () =>
    `whitespace-pre-line break-words ${valueSizeClasses[size()]} leading-[var(--leading-snug)] text-[color:var(--color-text)] ${
      props.inset ? insetClasses[size()] : ''
    }`.trim()

  return (
    <Show when={hasContent()}>
      <div
        role="group"
        aria-labelledby={props.label ? labelId : undefined}
        aria-label={props.label ? undefined : props['aria-label']}
        aria-describedby={props.hint ? hintId : undefined}
        class={`flex min-w-0 flex-col ${props.inset ? 'gap-1.5' : 'gap-1'} ${props.class ?? ''}`.trim()}
      >
        <Show when={props.label}>
          <span
            id={labelId}
            class={`text-[length:var(--text-sm)] font-[var(--font-medium)] ${
              props.mutedLabel
                ? 'text-[color:var(--color-text-muted)]'
                : 'text-[color:var(--color-text)]'
            }`}
          >
            {props.label}
          </span>
        </Show>
        <Show
          when={content()}
          fallback={
            <p class={valueClass()}>
              <Show when={hasText(props.value)} fallback={<EmptyValue />}>
                {props.value}
              </Show>
            </p>
          }
        >
          {content()}
        </Show>
        <Show when={props.hint}>
          <p id={hintId} class={`${FIELD_MESSAGE_METRICS} text-[color:var(--color-text-muted)]`}>
            {props.hint}
          </p>
        </Show>
      </div>
    </Show>
  )
}
